<template>
  <v-btn
    v-if="!mobileMenuMode"
    color="base-white"
    rounded
    class="language-button custom-rounded-icon d-none d-sm-flex"
  >
    {{ shortLocale }}

    <v-tooltip
      v-model="showTooltip"
      activator="parent"
      location="bottom"
    >
      {{ tooltip }}
    </v-tooltip>

    <v-menu
      v-model="showMenu"
      activator="parent"
      width="230"
      offset="18"
    >
      <v-card class="px-2">
        <v-list
          class="rounded-lg"
          select-strategy="classic"
          :selected="[modelValue]"
        >
          <v-list-item
            v-for="locale in locales"
            :key="locale"
            color="base-blue-70"
            link
            rounded="lg"
            :value="locale"
            :class="getLocaleClasses(locale)"
            :title="getTitle(locale)"
            :append-icon="getAppendIcon(locale)"
            @click="handleClickSelect(locale)"
          />
        </v-list>
      </v-card>
    </v-menu>
  </v-btn>

  <!-- Mobile Menu Item -->
  <v-menu
    v-else
    width="230"
    offset="12"
  >
    <template #activator="{ props }">
      <v-list-item
        v-bind="props"
        role="button"
        rounded="lg"
        :prepend-icon="mdiTranslate"
        :append-icon="mdiMenuDown"
        :title="tooltip"
        class="hoverable-locale-item"
      />
    </template>

    <v-card class="px-2">
      <v-list
        class="rounded-lg"
        select-strategy="classic"
        :selected="[modelValue]"
      >
        <v-list-item
          v-for="locale in locales"
          :key="locale"
          color="base-blue-70"
          link
          rounded="lg"
          :value="locale"
          :class="getLocaleClasses(locale)"
          :title="getTitle(locale)"
          :append-icon="getAppendIcon(locale)"
          @click="handleClickSelect(locale)"
        />
      </v-list>
    </v-card>
  </v-menu>
</template>
  

<script>
import { mdiCheck, mdiMenuDown, mdiTranslate } from '@mdi/js'
import langmap from 'langmap'


export default {
    name: 'CrvAppLocaleSwitcher',

    props: {
        modelValue: {
            type: String,
            default: 'nl', // Needs to be a valid locale from https://www.npmjs.com/package/langmap
            validator: (locale) => {
                if (!langmap[locale]) {
                    console.error(`The locale code ${locale} is not supported`)
                    return false
                }
                return true
            },
        },

        tooltip: {
            type: String,
            default: '',
        },

        locales: {
            // locales need to be present in this list https://www.npmjs.com/package/langmap for native name
            // each locale code needs to be supported by your local i18n module
            type: Array,
            default: () => [],
            validator: (locales) => {
                const unsupportedLocales = locales.filter(locale => !langmap[locale])
                if (unsupportedLocales.length > 0) {
                    console.error('The following locale codes are not supported: ', unsupportedLocales)
                    return false
                }
                return true
            },
        },

        mobileMenuMode: {
            type: Boolean,
            default: false,
            note: `Shows a list-item instead of a button when in mobile menu mode`,
        },

        offset: {
            type: Number,
            default: 0,
        },
    },

    emits: [
        'update:modelValue', 
    ],

    data() {
        return {
            showTooltip: false,
            showMenu: false,
            mdiTranslate,
            mdiMenuDown,
            mdiCheck,
        }
    },

    computed: {
        shortLocale() {
            const langCode = this.modelValue.split('-')[0]

            const exceptionMap = {
            'zh': 'ZH',
            'pt': 'PT',
            'en': 'EN',
            'nl': 'NL',
            'fr': 'FR',
            'de': 'DE',
            'es': 'ES',
            }

            return (exceptionMap[langCode] || langCode).toUpperCase()
        },
    },

    watch: {
        showMenu() {
            if (this.showMenu) {
                this.showTooltip = false
            }
        },
    },

    methods: {
        handleClickSelect(locale) {
            this.$emit('update:modelValue', locale)
        },

        getLocaleClasses(locale) {
            return {
                'is-selected': locale === this.modelValue,
            }
        },

        getTitle(locale) {
            return `${langmap?.[locale]?.nativeName || 'Unsupported'} (${locale.toUpperCase()})`
        },

        getAppendIcon(locale) {
            if (locale === this.modelValue) {
                return mdiCheck
            }
        },
    }
}
</script>

<style lang="scss" scoped>
.language-button {
    font-size: 16px;
}

.custom-rounded-icon {
  border-radius: 25px !important;
  min-width: 50px;
  width: 50px;
  min-height: 50px;
  height: 50px;
}
</style>
